import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteAskHelenaConversation,
  getAskHelenaConversationMessages,
  getAskHelenaConversations,
  pinAskHelenaConversation,
  rateAskHelenaMessage,
  renameAskHelenaConversation,
  stopAskHelenaConversation,
  unpinAskHelenaConversation,
} from "controller/api/helena/askHelenaConversations";
import { useCallback, useEffect, useRef } from "react";
import type {
  ApiErrorType,
  AskHelenaConversationMessage,
  AskHelenaConversationsPage,
  AskHelenaStopResult,
} from "shared";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

export const askHelenaConversationKeys = {
  /** Invalidation prefix — covers every page and every search term. */
  lists: () => ["askHelenaConversations", "list"] as const,
  /**
   * `search` is part of the key, not optional: without it React Query serves
   * the unfiltered list from cache for a filtered request, which looks exactly
   * like search silently not working.
   */
  list: (page: number, pageSize: number, search: string) =>
    ["askHelenaConversations", "list", page, pageSize, search] as const,
  messages: (conversationId: string) =>
    ["askHelenaConversations", "messages", conversationId] as const,
};

export const useAskHelenaConversations = ({
  page = DEFAULT_PAGE,
  pageSize = DEFAULT_PAGE_SIZE,
  search = "",
  enabled = true,
}: {
  page?: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean;
} = {}) => {
  return useQuery<AskHelenaConversationsPage>({
    queryKey: askHelenaConversationKeys.list(page, pageSize, search),
    queryFn: () => getAskHelenaConversations({ page, pageSize, search }),
    onError: (err) => debugConsole(err),
    enabled,
    // A debounced search term changes the key on every settled keystroke; without
    // this the list blanks between terms and the panel flickers.
    keepPreviousData: true,
  });
};

export const useAskHelenaConversationMessages = ({
  conversationId,
  enabled = true,
}: {
  conversationId: string;
  enabled?: boolean;
}) => {
  return useQuery<AskHelenaConversationMessage[]>({
    queryKey: askHelenaConversationKeys.messages(conversationId),
    queryFn: () => getAskHelenaConversationMessages({ conversationId }),
    onError: (err) => debugConsole(err),
    enabled: enabled && !!conversationId,
    // Nothing is kept after the thread closes: the stored copy goes stale the
    // moment the user asks another question, and re-opening a thread has to
    // show what the service actually holds.
    cacheTime: 0,
    // A 404 is a normal terminal answer here (unknown id, or someone else's
    // thread — the service does not distinguish the two), so retrying only
    // delays the error. The app-level retry predicate reads
    // `error.response.status`, which APIRequestError does not carry, so it
    // cannot opt out on its own.
    retry: false,
  });
};

/**
 * Invalidates the sidebar's conversation list. Called when a turn ends, because
 * a first question creates a conversation that the list has never seen — and,
 * now that the service names threads itself, because that is also when the
 * generated title lands. ONE invalidation, never a poll.
 *
 * Cache writes belong to the controller layer — the feature only says "this
 * changed", it does not know the query keys.
 */
export const useRefreshAskHelenaConversations = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    void queryClient.invalidateQueries(askHelenaConversationKeys.lists());
  }, [queryClient]);
};

export const useRefreshAskHelenaConversationMessages = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (conversationId: string) =>
      queryClient.fetchQuery<AskHelenaConversationMessage[]>({
        queryKey: askHelenaConversationKeys.messages(conversationId),
        queryFn: () => getAskHelenaConversationMessages({ conversationId }),
        cacheTime: 0,
        staleTime: 0,
        retry: false,
      }),
    [queryClient],
  );
};

const TITLE_REFETCH_DELAYS_MS = [1500, 2500];

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const isTitleSettled = (
  queryClient: QueryClient,
  conversationId: string,
): boolean => {
  const pages = queryClient.getQueriesData<AskHelenaConversationsPage>(
    askHelenaConversationKeys.lists(),
  );
  for (const [, page] of pages) {
    const row = page?.items.find((item) => item.id === conversationId);
    if (row) return Boolean(row.title) || row.titleSource === "manual";
  }
  return false;
};

export const useRefreshAskHelenaConversationTitle = () => {
  const queryClient = useQueryClient();
  const runId = useRef(0);

  useEffect(
    () => () => {
      runId.current += 1;
    },
    [],
  );

  return useCallback(
    async (conversationId: string | null) => {
      if (!conversationId) return;

      runId.current += 1;
      const thisRun = runId.current;

      for (const ms of TITLE_REFETCH_DELAYS_MS) {
        if (isTitleSettled(queryClient, conversationId)) return;
        await delay(ms);
        if (runId.current !== thisRun) return;
        await queryClient.invalidateQueries(askHelenaConversationKeys.lists());
        if (runId.current !== thisRun) return;
      }
    },
    [queryClient],
  );
};

const logAskHelenaError = (error: ApiErrorType, functionName: string) => {
  debugConsole(error);
  sentryLogger({ error, workflow: "helena", level: "error", functionName });
};

/**
 * Pin, unpin and delete all INVALIDATE rather than patching the cache.
 *
 * The response carries the updated summary, so a hand-merge is tempting — but
 * the list's ordering is two SQL sort keys with a null-handling rule (pinned
 * first by `pinnedAt` desc, then everything else newest-first). Reproducing
 * that client-side is precisely the mistake that makes pinning look broken. One
 * refetch of a <=20-row list is cheaper than an ordering bug.
 */
export const usePinAskHelenaConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: pinAskHelenaConversation,
    onSuccess: () =>
      queryClient.invalidateQueries(askHelenaConversationKeys.lists()),
    onError: (error: ApiErrorType) =>
      logAskHelenaError(error, "pinAskHelenaConversation"),
  });
};

export const useUnpinAskHelenaConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unpinAskHelenaConversation,
    onSuccess: () =>
      queryClient.invalidateQueries(askHelenaConversationKeys.lists()),
    onError: (error: ApiErrorType) =>
      logAskHelenaError(error, "unpinAskHelenaConversation"),
  });
};

/** Every cached page of the list, whatever its search term. */
type CachedConversationLists = [
  QueryKey,
  AskHelenaConversationsPage | undefined,
][];

/**
 * Patches a renamed thread's label in place across every cached list page.
 *
 * Safe to do optimistically precisely because a rename does NOT move the row:
 * the ordering keys are `pinnedAt` and `createdAt`, and neither changes. That
 * is what separates this from pin/unpin, which must round-trip.
 */
const patchRenamedConversation = (
  queryClient: QueryClient,
  conversationId: string,
  title: string,
) => {
  queryClient.setQueriesData<AskHelenaConversationsPage>(
    askHelenaConversationKeys.lists(),
    (page) => {
      if (!page) return page;
      return {
        ...page,
        items: page.items.map((conversation) =>
          conversation.id === conversationId
            ? { ...conversation, title, titleSource: "manual" as const }
            : conversation,
        ),
      };
    },
  );
};

export const useRenameAskHelenaConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: renameAskHelenaConversation,
    onMutate: async ({ conversationId, title }) => {
      // An in-flight list refetch would land after the patch and undo it.
      await queryClient.cancelQueries(askHelenaConversationKeys.lists());
      const previous: CachedConversationLists = queryClient.getQueriesData(
        askHelenaConversationKeys.lists(),
      );
      patchRenamedConversation(queryClient, conversationId, title.trim());
      return { previous };
    },
    onError: (error: ApiErrorType, _variables, context) => {
      for (const [queryKey, page] of context?.previous ?? []) {
        queryClient.setQueryData(queryKey, page);
      }
      logAskHelenaError(error, "renameAskHelenaConversation");
    },
    onSettled: () =>
      queryClient.invalidateQueries(askHelenaConversationKeys.lists()),
  });
};

export const useRateAskHelenaMessage = () =>
  useMutation({
    mutationFn: rateAskHelenaMessage,
    onError: (error: ApiErrorType) =>
      logAskHelenaError(error, "rateAskHelenaMessage"),
  });

export const useStopAskHelenaTurn = () =>
  useCallback(
    async (conversationId: string): Promise<AskHelenaStopResult | null> => {
      try {
        return await stopAskHelenaConversation({ conversationId });
      } catch (error: unknown) {
        logAskHelenaError(error as ApiErrorType, "stopAskHelenaConversation");
        return null;
      }
    },
    [],
  );

export const useDeleteAskHelenaConversation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAskHelenaConversation,
    onSuccess: (_data, { conversationId }) => {
      void queryClient.invalidateQueries(askHelenaConversationKeys.lists());
      // The thread's messages endpoint answers 404 from here on, so the cached
      // copy would only ever be served as a phantom.
      queryClient.removeQueries(
        askHelenaConversationKeys.messages(conversationId),
      );
    },
    onError: (error: ApiErrorType) =>
      logAskHelenaError(error, "deleteAskHelenaConversation"),
  });
};
